import { AnalyticsService } from '@alison/analytics/client';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { useLeadingKpiOptions } from '../../lib/leadingKpi.hooks';
import { ActionsYouCanTakeSection } from './ActionsYouCanTakeSection';
import {
  CreativeApproachCardsSection,
  type CreativeApproachDetailCardInput,
} from './CreativeApproachCardsSection';
import { useElementsCategories, useElementsCategoryExamples } from './data/api';
import { DEFAULT_CATEGORY_ICON } from './data/categoryVisuals';
import { CREATIVE_APPROACH_TEMPLATES } from './data/creativeApproachContent';
import { HierarchyStructureSection } from './HierarchyStructureSection';
import { HowElementsExplorerHeader } from './HowElementsExplorerHeader';
import {
  ConceptualCategoryIcon,
  GraphicsCategoryIcon,
  ProductCategoryIcon,
  StepOneIcon,
  StepTwoIcon,
  StoryCategoryIcon,
  StructureCategoryIcon,
} from './icons/HowElementsExplorerIcons';

interface HowElementsExplorerWorksProps {
  accountIds: number[];
  dateRange: [string, string];
  filters: Record<string, string[]> | undefined;
  selectedCategory: string | null;
  onSelectCategory: (category: string) => void;
  leadingKpi: string;
  totalCreatives: number | null;
}

function categoryDetailIcon(category: string, color: string): ReactNode {
  switch (category) {
    case 'Product':
      return <ProductCategoryIcon color={color} />;
    case 'Structure':
      return <StructureCategoryIcon color={color} />;
    case 'Story':
      return <StoryCategoryIcon color={color} />;
    case 'Graphics':
      return <GraphicsCategoryIcon color={color} />;
    case 'Conceptual':
      return <ConceptualCategoryIcon color={color} />;
    default:
      return <DEFAULT_CATEGORY_ICON fontSize="small" />;
  }
}

export function HowElementsExplorerWorks({
  accountIds,
  dateRange,
  filters,
  selectedCategory,
  onSelectCategory,
  leadingKpi,
  totalCreatives,
}: HowElementsExplorerWorksProps): ReactNode {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCreativeApproachDetails, setShowCreativeApproachDetails] = useState(false);

  const scope = { dateRange, accountIds, filters };
  const categories = useElementsCategories(scope, true);
  const examples = useElementsCategoryExamples(
    { ...scope, kpi: leadingKpi },
    isExpanded || showCreativeApproachDetails,
  );
  const { data: leadingKpiOptions } = useLeadingKpiOptions(accountIds);
  const leadingKpiLabel =
    leadingKpiOptions?.find((option) => option.id === leadingKpi)?.label ??
    leadingKpi.toUpperCase();

  const detailCardsLoading = showCreativeApproachDetails && examples.data === undefined;
  const categoriesLoading = categories.data === undefined;

  const examplesByCategory = new Map(
    (examples.data?.categories ?? []).map((row) => [row.category, row]),
  );

  function getDetailCardProps(category: string): CreativeApproachDetailCardInput | null {
    const template = CREATIVE_APPROACH_TEMPLATES.find((item) => item.category === category);
    if (!template) return null;
    const topCreative = examplesByCategory.get(category)?.topCreative ?? null;
    const accentColor =
      theme.palette.elementCategory[category] ?? theme.palette.elementCategory.default;
    return {
      icon: categoryDetailIcon(category, accentColor),
      title: category,
      creatives: examplesByCategory.get(category)?.creativesCount ?? null,
      sectionTitle: template.sectionTitle,
      sectionDescription: template.sectionDescription,
      categoryTitle: category,
      bullets: template.bullets,
      accentColor,
      elements: topCreative?.elements ?? [],
      thumbnailUrl: topCreative?.thumbnailUrl ?? null,
      previewUrl: topCreative?.previewUrl ?? null,
      duration: topCreative?.duration ?? null,
      creativeType: topCreative?.creativeType ?? null,
      leadingKpi: topCreative?.kpiValue ?? null,
      leadingKpiLabel,
    };
  }

  function handleToggleExpand(): void {
    const next = !isExpanded;
    AnalyticsService.track('Elements Explorer - Toggle How It Works', { expanded: next });
    setIsExpanded(next);
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 3,
          width: '100%',
        }}
      >
        <HowElementsExplorerHeader isExpanded={isExpanded} onToggleExpand={handleToggleExpand} />
        {isExpanded ? <Divider sx={{ mt: 4 }} /> : null}

        {isExpanded ? (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 3 }}>
              <StepOneIcon />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <HierarchyStructureSection />
                <CreativeApproachCardsSection
                  categories={categories.data ?? []}
                  categoriesLoading={categoriesLoading}
                  selectedCategory={selectedCategory}
                  onSelectCategory={onSelectCategory}
                  totalCreatives={totalCreatives}
                  showDetailCards={showCreativeApproachDetails}
                  detailCardsLoading={detailCardsLoading}
                  onToggleDetailCards={() => setShowCreativeApproachDetails((prev) => !prev)}
                  getDetailCardProps={getDetailCardProps}
                />
              </Box>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 3 }}>
              <StepTwoIcon />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: 'text.primary', fontSize: 16, fontWeight: 700, mt: 1 }}>
                  Actions You Can Take
                </Typography>
                <ActionsYouCanTakeSection />
                {showCreativeApproachDetails ? (
                  <ButtonBase
                    onClick={() => {
                      AnalyticsService.track('Elements Explorer - Toggle How It Works', {
                        expanded: false,
                      });
                      setIsExpanded(false);
                    }}
                    sx={{ color: 'primary.main', mt: 3, width: '100%' }}
                  >
                    <Typography sx={{ color: 'primary.main', fontSize: 14, fontWeight: 500 }}>
                      Hide explanation
                    </Typography>
                  </ButtonBase>
                ) : null}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
      {isExpanded ? null : (
        <CreativeApproachCardsSection
          categories={categories.data ?? []}
          categoriesLoading={categoriesLoading}
          selectedCategory={selectedCategory}
          onSelectCategory={onSelectCategory}
          totalCreatives={totalCreatives}
          showDetailCards={false}
          detailCardsLoading={false}
          onToggleDetailCards={() => setShowCreativeApproachDetails(false)}
          getDetailCardProps={getDetailCardProps}
          collapsedPreview
          showMargin={false}
        />
      )}
    </>
  );
}
